/**
 * Formatting utility functions
 * 날짜, 전화번호, 금액 등이 포맷팅 유틸리티
 */

/**
 * 날짜를 한국 형식으로 포맷팅
 * @param dateString - ISO 날짜 문자열
 * @returns 포맷팅된 날짜 문자열 (예: 2024.01.01 (월) 오전 10:30)
 */
export const formatDate = (dateString: string): string => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString("ko-KR", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        weekday: "short",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
    });
};

/**
 * 전화번호를 하이픈 포맷으로 변환
 * @param phone - 전화번호 문자열
 * @returns 포맷팅된 전화번호 (예: 010-1234-5678)
 */
export const formatPhoneNumber = (phone: string): string => {
    if (!phone) return "";
    // 숫자만 추출
    const numbers = phone.replace(/\D/g, "");
    // 010-1234-5678 형식으로 포맷팅
    if (numbers.length === 11) {
        return `${numbers.slice(0, 3)}-${numbers.slice(3, 7)}-${numbers.slice(7)}`;
    } else if (numbers.length === 10) {
        return `${numbers.slice(0, 3)}-${numbers.slice(3, 6)}-${numbers.slice(6)}`;
    }
    return phone;
};

/**
 * 생년월일을 YYYY-MM-DD 포맷으로 변환
 * @param birthDate - 생년월일 문자열 (YYYYMMDD)
 * @returns 포맷팅된 생년월일 (예: 1990-01-01)
 */
export const formatBirthDate = (birthDate: string): string => {
    if (!birthDate) return "";
    // YYYY-MM-DD 형식으로 포맷팅
    if (birthDate.length === 8) {
        return `${birthDate.slice(0, 4)}-${birthDate.slice(4,6)}-${birthDate.slice(6)}`;
    }
    return birthDate;
};

/**
 * 주민등록번호를 하이픈 포맷으로 변환
 * @param residentNumber - 주민등록번호 문자열
 * @returns 포맷팅된 주민등록번호 (예: 123456-1234567)
 */
export const formatResidentNumber = (residentNumber: string): string => {
    if (!residentNumber) return "";
    // 숫자만 추출
    const numbers = residentNumber.replace(/\D/g, "");
    // 123456-1234567 형식으로 포맷팅
    if (numbers.length === 13) {
        return `${numbers.slice(0, 6)}-${numbers.slice(6)}`;
    }
    return residentNumber;
};

/**
 * 계약 상태 코드를 한글로 변환
 * @param status - 계약 상태 코드
 * @returns 한글 계약 상태 (예: 계약중, 계약종료)
 */
export const formatContractStatus = (status: string): string => {
    if (!status) return "";
    if (status === "UNDER_CONTRACT") {
        return "계약중";
    } else if (status === "TERMINATED_CONTRACT") {
        return "계약종료";
    }
    return status;
};

/**
 * 금액을 통화 형식으로 포맷팅
 * @param amount - 금액
 * @returns 포맷팅된 금액 문자열 (예: 1,000,000)
 */
export const formatCurrency = (amount: number | string): string => {
    if (!amount && amount !== 0) return "";
    const numAmount = typeof amount === "string" ? parseFloat(amount) : amount;
    return numAmount.toLocaleString("ko-KR");
};

/**
 * 숫자 포맷팅 (천 단위 콤마) - null/undefined 처리
 */
export function formatNumber(value: number | null | undefined): string {
  if (value === null || value === undefined) return '-';
  return new Intl.NumberFormat('ko-KR').format(value);
}

/**
 * 날짜 문자열을 표시용 형식으로 변환
 * @param dateString ISO 날짜 문자열 (YYYY-MM-DD 또는 YYYY-MM-DDTHH:mm:ss)
 * @returns YYYY-MM-DD 형식
 */
export function formatDateToDisplay(dateString: string | null | undefined | any): string {
  if (!dateString) return '-';
  if (typeof dateString === 'string') {
    return dateString.split('T')[0];
  }
  // 배열 형식 처리 [2024, 11, 21]
  if (Array.isArray(dateString)) {
    const [year, month, day] = dateString;
    return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  }
  return '-';
}

/**
 * 날짜시간 문자열을 표시용 형식으로 변환
 * @param dateTimeString ISO 날짜시간 문자열
 * @returns YYYY-MM-DD HH:mm 형식
 */
export function formatDateTimeToDisplay(dateTimeString: string | null | undefined): string {
  if (!dateTimeString) return '-';
  return dateTimeString.replace('T', ' ').substring(0, 16);
}

/**
 * 퍼센트 포맷팅
 */
export function formatPercent(value: number | null | undefined, decimals = 1): string {
  if (value === null || value === undefined) return '-';
  return `${value.toFixed(decimals)}%`;
}

/**
 * 금액을 원 단위로 표시
 */
export function formatAmount(value: number | null | undefined): string {
  if (value === null || value === undefined) return '-';
  return `${formatCurrency(value)}원`;
}

/**
 * 사업자등록번호를 하이픈 포맷으로 변환
 * @param businessNumber - 사업자등록번호 문자열
 * @returns 포맷팅된 사업자등록번호 (예: 123-45-67890)
 */
export const formatBusinessNumber = (businessNumber: string): string => {
    if (!businessNumber) return "";
    // 숫자만 추출
    const numbers = businessNumber.replace(/\D/g, "");
    // 123-45-67890 형식으로 포맷팅
    if (numbers.length === 10) {
        return `${numbers.slice(0, 3)}-${numbers.slice(3, 5)}-${numbers.slice(5)}`;
    }
    return businessNumber;
};
